/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useApiData } from "./ApiData";
import { Survey } from "@/types";

type SurveyContextType = {
  survey: Survey | null;
  isOwner: boolean;
  canEdit: boolean;
  isShared: boolean;
  reload: () => Promise<void>;
  setSurvey: (survey: Survey | null) => void;
};

const SurveyContext = createContext<SurveyContextType>({
  survey: null,
  isOwner: false,
  canEdit: false,
  isShared: false,
  reload: async () => {},
  setSurvey: () => {},
});

type SurveyProviderProps = {
  children: ReactNode;
  surveyId: string;
};

export const useSurvey = (): SurveyContextType => {
  const ctx = useContext(SurveyContext);

  return ctx;
};

export const SurveyProvider: React.FC<SurveyProviderProps> = ({ children, surveyId }) => {
  const { get } = useApiData();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [isShared, setIsShared] = useState<boolean>(false);

  const applySurvey = useCallback(
    (nextSurvey: Survey | null) => {
      setSurvey(nextSurvey);
      if (!nextSurvey) {
        setIsOwner(false);
        setCanEdit(false);
        setIsShared(false);
        return;
      }
      setIsOwner(!!nextSurvey.isOwner);
      setCanEdit(!!nextSurvey.canEdit);
      setIsShared(!!nextSurvey.isShared);
    },
    []
  );

  const reload = useCallback(async () => {
    const result = await get(`/survey/${surveyId}`);
    if (result) {
      applySurvey(result);
    }
  }, [applySurvey, get, surveyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return <SurveyContext.Provider value={{ survey, isOwner, canEdit, isShared, reload, setSurvey: applySurvey }}>{children}</SurveyContext.Provider>;
};
